/**
 * Diálogos da cena: propriedades do token e configuração da cena (grade,
 * tamanho, escala, imagem de fundo). Cada "Salvar" vira um lote só no NDB.
 */
import { useState } from 'react';
import { uploadRoomFile, useFileUrl } from '../files.js';
import { useLanguage, useT } from '../i18n/index.js';
import type { AttrValue, Op } from '../ndb.js';
import { useApp } from '../store.js';
import { Button, CheckBox, ComboBox, Dialog, TextEdit } from '../ui/controls.js';
import { ImagePickerDialog, rpgDeskFileId } from '../ui/ImagePicker.js';
import { TOKEN_CONDITIONS, type TokenCondition } from '../shared.js';
import { ConditionIcon, useConditionLabel } from './conditions.js';
import { askConfirm } from '../ui/prompt.js';
import { toast } from '../ui/Toaster.js';
import { PRESET_ORDER, WALL_PRESETS, doorStateOps, presetOf, setWallPropsOps, type DoorKind, type Wall, type WallDir, type WallPreset, type WallProps, type WallSight } from './walls.js';
import {
  AURA_GRID_SHAPES,
  AURA_MAX_CELLS,
  AURA_OPACITY,
  AURA_SHAPES,
  SCENE_DEFAULTS,
  TOKEN_COLORS,
  auraOps,
  clearBackgroundOps,
  conditionOps,
  readPermOps,
  newAura,
  resetMemoryOps,
  setControllerOps,
  setOps,
  sightOps,
  webImageUrl,
  type AuraShape,
  type FacingMode,
  type SceneModel,
  type SceneToken,
  type TokenAura,
  type TokenLayer,
  type TokenSight,
} from './model.js';

const SIZES = ['0.5', '1', '2', '3', '4', '6'];

/** Número digitado (vírgula ou ponto); vazio ou inválido volta ao valor de antes. */
const decimal = (v: string, fallback: number, lo = 0, hi = 100_000): number => {
  const n = Number(v.trim().replace(',', '.'));
  return v.trim() !== '' && Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : fallback;
};
/** Número para mostrar num campo: vírgula decimal em português, ponto em inglês. */
const show = (n: number) => (useLanguage.getState().language === 'en' ? String(n) : String(n).replace('.', ','));

/** As cores de sempre, para escolher uma. */
function Swatches({ value, onChange, label }: { value: string; onChange: (color: string) => void; label: string }) {
  return (
    <div className="rd-swatches" role="radiogroup" aria-label={label}>
      {TOKEN_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={value === c}
          aria-label={c}
          className={`rd-swatch${value === c ? ' is-on' : ''}`}
          style={{ background: c }}
          onClick={() => onChange(c)}
        />
      ))}
    </div>
  );
}

/** A aura como está no diálogo (os números como a pessoa digitou). */
type AuraDraft = {
  on: boolean;
  shape: AuraShape;
  reach: string;
  angle: string;
  w: string;
  h: string;
  color: string;
  opacity: number;
  grid: boolean;
  fileId: string | null;
  url: string | null;
};

const auraDraft = (aura: TokenAura | null, cellDistance: number): AuraDraft => {
  const a = aura ?? newAura(cellDistance);
  return { on: !!aura, shape: a.shape, reach: show(a.reach), angle: show(a.angle), w: show(a.w), h: show(a.h), color: a.color, opacity: a.opacity, grid: a.grid, fileId: a.fileId, url: a.url };
};

/** A aura que o diálogo grava (null: sem aura). Número inválido volta ao de antes. */
function auraFromDraft(d: AuraDraft, before: TokenAura | null, cellDistance: number): TokenAura | null {
  if (!d.on || (d.shape === 'image' && !d.fileId && !d.url)) return null;
  const prev = before ?? newAura(cellDistance);
  const cell = cellDistance > 0 ? cellDistance : 1;
  const max = AURA_MAX_CELLS * cell;
  const image = d.shape === 'image';
  return {
    shape: d.shape,
    reach: decimal(d.reach, prev.reach, 0, max),
    angle: decimal(d.angle, prev.angle, 10, 180),
    w: decimal(d.w, prev.w, 0.05 * cell, max),
    h: decimal(d.h, prev.h, 0.05 * cell, max),
    color: d.color,
    opacity: d.opacity,
    grid: d.grid && AURA_GRID_SHAPES.includes(d.shape),
    fileId: image ? d.fileId : null,
    url: image && !d.fileId ? d.url : null,
  };
}

/**
 * A aura do token (docs/spec/scene-auras.md): o formato, o tamanho na unidade da cena, a cor, a
 * opacidade e, nos formatos redondos, "Em quadrados". O mestre e quem controla o token mudam.
 */
function AuraFields({ draft, onChange, unit, onPickImage }: { draft: AuraDraft; onChange: (next: AuraDraft) => void; unit: string; onPickImage: () => void }) {
  const t = useT();
  const preview = useFileUrl(draft.fileId);
  const set = (patch: Partial<AuraDraft>) => onChange({ ...draft, ...patch });
  // a imagem quase opaca e a forma translúcida: trocar entre elas troca a opacidade inicial
  const setShape = (shape: AuraShape) => {
    const toImage = shape === 'image' && draft.opacity === AURA_OPACITY.shape;
    const fromImage = shape !== 'image' && draft.shape === 'image' && draft.opacity === AURA_OPACITY.image;
    set({ shape, ...(toImage ? { opacity: AURA_OPACITY.image } : fromImage ? { opacity: AURA_OPACITY.shape } : {}) });
  };
  const sized = draft.shape === 'rect' || draft.shape === 'image';
  const imageSrc = draft.fileId ? (typeof preview === 'string' ? preview : null) : draft.url;
  return (
    <fieldset className="rd-sceneForm__group">
      <legend>{t('scene.aura')}</legend>
      <CheckBox checked={draft.on} onChange={(on) => set({ on })}>
        {t('scene.aura.has')}
      </CheckBox>
      {draft.on ? (
        <>
          <div className="rd-sceneForm__grid">
            <label className="rd-sceneForm__field">
              <span>{t('scene.aura.shape')}</span>
              <ComboBox<AuraShape> value={draft.shape} onChange={setShape} options={AURA_SHAPES.map((shape) => ({ value: shape, label: t(`scene.aura.shape.${shape}`) }))} />
            </label>
            {sized ? (
              <>
                <label className="rd-sceneForm__field">
                  <span>{t('scene.aura.width', { unit })}</span>
                  <TextEdit look="filled" value={draft.w} onChange={(w) => set({ w })} inputMode="decimal" maxLength={8} />
                </label>
                <label className="rd-sceneForm__field">
                  <span>{t('scene.aura.height', { unit })}</span>
                  <TextEdit look="filled" value={draft.h} onChange={(h) => set({ h })} inputMode="decimal" maxLength={8} />
                </label>
              </>
            ) : (
              <label className="rd-sceneForm__field">
                <span>{t('scene.aura.reach', { unit })}</span>
                <TextEdit look="filled" value={draft.reach} onChange={(reach) => set({ reach })} inputMode="decimal" maxLength={8} />
              </label>
            )}
            {draft.shape === 'cone' ? (
              <label className="rd-sceneForm__field">
                <span>{t('scene.aura.angle')}</span>
                <TextEdit look="filled" value={draft.angle} onChange={(angle) => set({ angle })} inputMode="decimal" maxLength={5} />
              </label>
            ) : null}
          </div>
          {sized ? null : <p className="rd-sceneForm__hint">{t('scene.aura.reachHint')}</p>}
          {draft.shape === 'image' ? (
            <div className="rd-sceneForm__inline">
              {imageSrc ? <img className="rd-sceneForm__auraImage" src={imageSrc} alt="" /> : null}
              <Button variant="secondary" onClick={onPickImage}>
                {t('scene.aura.image')}
              </Button>
              {!draft.fileId && !draft.url ? <span className="rd-sceneForm__hint is-warning">{t('scene.aura.image.missing')}</span> : null}
            </div>
          ) : (
            <div className="rd-sceneForm__field">
              <span>{t('scene.aura.color')}</span>
              <Swatches value={draft.color} onChange={(color) => set({ color })} label={t('scene.aura.color')} />
            </div>
          )}
          <label className="rd-sceneForm__field">
            <span>
              {t('scene.aura.opacity')} — {Math.round(draft.opacity * 100)}%
            </span>
            <input className="rd-range" type="range" min={0.1} max={1} step={0.05} value={draft.opacity} onChange={(e) => set({ opacity: Number(e.target.value) })} />
          </label>
          {AURA_GRID_SHAPES.includes(draft.shape) ? (
            <CheckBox checked={draft.grid} onChange={(grid) => set({ grid })}>
              {t('scene.aura.grid')}
            </CheckBox>
          ) : null}
          <p className="rd-sceneForm__hint">{t('scene.aura.hint')}</p>
        </>
      ) : null}
    </fieldset>
  );
}

export function TokenDialog({
  token,
  isGm,
  unit,
  cellDistance,
  onClose,
  write,
  newId,
  setInvisible,
}: {
  token: SceneToken;
  isGm: boolean;
  unit: string;
  cellDistance: number;
  onClose: () => void;
  write: (ops: Op[]) => void;
  newId: () => string;
  /** liga ou desliga o Invisível, pelo servidor (a lista de quem vê fica no diálogo dele) */
  setInvisible: (viewers: string[] | null) => void;
}) {
  const t = useT();
  const conditionLabel = useConditionLabel();
  const users = useApp((s) => s.users);
  const members = useApp((s) => s.members);
  const me = useApp((s) => s.user);
  const [name, setName] = useState(token.name);
  const [size, setSize] = useState(String(token.size));
  const [color, setColor] = useState(token.color);
  const [fileId, setFileId] = useState(token.fileId);
  const [hp, setHp] = useState(token.hp === null ? '' : String(token.hp));
  const [hpMax, setHpMax] = useState(token.hpMax === null ? '' : String(token.hpMax));
  const [showName, setShowName] = useState(token.showName);
  const [layer, setLayer] = useState<TokenLayer>(token.layer);
  const [hidden, setHidden] = useState(token.hidden);
  const [controllers, setControllers] = useState<string[]>(token.controllers);
  // as condições (BRAZ-25) e o Invisível (BRAZ-24)
  const [conditions, setConditions] = useState<TokenCondition[]>(token.conditions);
  const [invisible, setInvisibleOn] = useState(!!token.invisible);
  const [uploading, setUploading] = useState(false);
  const preview = useFileUrl(fileId);
  // visão e luz (só o mestre muda)
  const [hasSight, setHasSight] = useState(token.sight.has);
  const [bright, setBright] = useState(show(token.sight.bright));
  const [dim, setDim] = useState(show(token.sight.dim));
  const [dark, setDark] = useState(show(token.sight.dark));
  const [lightBright, setLightBright] = useState(show(token.sight.lightBright));
  const [lightDim, setLightDim] = useState(show(token.sight.lightDim));
  const [lightOnly, setLightOnly] = useState(token.lightOnly);
  const [angle, setAngle] = useState(show(token.sight.angle));
  const [lightAngle, setLightAngle] = useState(show(token.sight.lightAngle));
  const [facingMode, setFacingMode] = useState(token.sight.facingMode);
  // a aura (o mestre e quem controla o token)
  const [aura, setAura] = useState(() => auraDraft(token.aura, cellDistance));
  const [pickingAura, setPickingAura] = useState(false);
  const auraMissingImage = aura.on && aura.shape === 'image' && !aura.fileId && !aura.url;

  const people = [
    ...users.filter((u) => u.id !== me?.id || !isGm).map((u) => ({ id: u.id, name: members[u.id]?.nick ?? u.displayName })),
    ...controllers.filter((id) => !users.some((u) => u.id === id)).map((id) => ({ id, name: t('scene.token.offline') })),
  ];

  const save = () => {
    const attrs: Record<string, AttrValue | null> = {
      name: name.trim().slice(0, 60),
      size: Number(size) || 1,
      color,
      fileId: fileId ?? null,
      hp: hp.trim() === '' ? null : Number(hp) || 0,
      hpMax: hpMax.trim() === '' ? null : Number(hpMax) || 0,
      showName,
    };
    if (isGm) Object.assign(attrs, { layer, hidden, lightOnly: lightOnly || null });
    // só o que mudou (escrever igual também dispararia eventos à toa)
    const current: Record<string, AttrValue | null> = {
      name: token.name,
      size: token.size,
      color: token.color,
      fileId: token.fileId,
      hp: token.hp,
      hpMax: token.hpMax,
      showName: token.showName,
      layer: token.layer,
      hidden: token.hidden,
      lightOnly: token.lightOnly || null,
    };
    const changed = Object.fromEntries(Object.entries(attrs).filter(([k, v]) => v !== current[k]));
    const ops: Op[] = setOps(token.id, changed);
    // oculto, camada e quem controla mudam quem recebe o token (com o Invisível, BRAZ-24)
    const sameControllers = controllers.length === token.controllers.length && controllers.every((id) => token.controllers.includes(id));
    if (isGm && (hidden !== token.hidden || layer !== token.layer || !sameControllers)) ops.push(...readPermOps(token, { hidden, layer, controllers }));
    if (isGm) {
      for (const id of controllers) if (!token.controllers.includes(id)) ops.push(...setControllerOps(token.id, id, true));
      for (const id of token.controllers) if (!controllers.includes(id)) ops.push(...setControllerOps(token.id, id, false));
      const sight: TokenSight = {
        ...token.sight,
        has: hasSight,
        bright: decimal(bright, token.sight.bright),
        dim: decimal(dim, token.sight.dim),
        dark: decimal(dark, token.sight.dark),
        lightBright: decimal(lightBright, token.sight.lightBright),
        lightDim: decimal(lightDim, token.sight.lightDim),
        angle: decimal(angle, token.sight.angle, 10, 360),
        lightAngle: decimal(lightAngle, token.sight.lightAngle, 10, 360),
        facingMode,
      };
      ops.push(...sightOps(token, sight, newId));
    }
    ops.push(...auraOps(token, auraFromDraft(aura, token.aura, cellDistance)));
    for (const id of TOKEN_CONDITIONS) if (conditions.includes(id) !== token.conditions.includes(id)) ops.push(...conditionOps(token.id, id, conditions.includes(id)));
    write(ops);
    // ligado aqui, só quem sempre vê continua vendo; os liberados de antes voltam
    if (invisible !== !!token.invisible) setInvisible(invisible ? (token.invisible ?? []) : null);
    onClose();
  };

  const pick = (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    uploadRoomFile(file, 'token')
      .then((stored) => setFileId(stored.id))
      .catch((error: unknown) => toast(t('scene.uploadFailed', { message: error instanceof Error ? error.message : String(error) }), 'error'))
      .finally(() => setUploading(false));
  };

  return (
    <>
      <Dialog
        title={t('scene.token.properties')}
        onClose={onClose}
        wide
        actions={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t('create.cancel')}
            </Button>
            <Button variant="action" onClick={save} disabled={uploading || !name.trim() || auraMissingImage}>
              {t('scene.save')}
            </Button>
          </>
        }
      >
        <form
          className="rd-sceneForm"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <div className="rd-sceneForm__row">
            <div className="rd-sceneForm__avatar" style={{ background: color }}>
              {fileId && typeof preview === 'string' ? <img src={preview} alt="" /> : <span>{name.trim().slice(0, 2).toUpperCase() || '?'}</span>}
            </div>
            <div className="rd-sceneForm__col">
              <label className="rd-sceneForm__field">
                <span>{t('scene.token.name')}</span>
                <TextEdit look="filled" value={name} onChange={setName} maxLength={60} autoFocusOnMount />
              </label>
              <div className="rd-sceneForm__inline">
                <label className="rd-sceneForm__file rd-btn rd-btn--secondary">
                  {uploading ? t('scene.uploading') : t('scene.token.image')}
                  <input type="file" accept="image/*" hidden onChange={(e) => pick(e.target.files?.[0])} />
                </label>
                {fileId ? (
                  <Button variant="link" onClick={() => setFileId(null)}>
                    {t('scene.token.noImage')}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="rd-sceneForm__field">
            <span>{t('scene.token.color')}</span>
            <Swatches value={color} onChange={setColor} label={t('scene.token.color')} />
          </div>

          <div className="rd-sceneForm__grid">
            <label className="rd-sceneForm__field">
              <span>{t('scene.token.size')}</span>
              <ComboBox
                value={SIZES.includes(size) ? size : '1'}
                onChange={setSize}
                options={SIZES.map((v) => ({ value: v, label: t('scene.token.sizeCells', { n: show(Number(v)) }) }))}
              />
            </label>
            <label className="rd-sceneForm__field">
              <span>{t('scene.token.hp')}</span>
              <TextEdit look="filled" value={hp} onChange={setHp} inputMode="numeric" maxLength={7} />
            </label>
            <label className="rd-sceneForm__field">
              <span>{t('scene.token.hpMax')}</span>
              <TextEdit look="filled" value={hpMax} onChange={setHpMax} inputMode="numeric" maxLength={7} />
            </label>
          </div>

          <CheckBox checked={showName} onChange={setShowName}>
            {t('scene.token.showName')}
          </CheckBox>

          <fieldset className="rd-sceneForm__group">
            <legend>{t('scene.token.conditions')}</legend>
            <div className="rd-sceneForm__people">
              {TOKEN_CONDITIONS.map((id) => (
                <CheckBox key={id} checked={conditions.includes(id)} onChange={(on) => setConditions((c) => (on ? [...c, id] : c.filter((x) => x !== id)))}>
                  <span className="rd-sceneForm__cond">
                    <ConditionIcon id={id} />
                    {conditionLabel(id)}
                  </span>
                </CheckBox>
              ))}
              <CheckBox checked={invisible} onChange={setInvisibleOn}>
                <span className="rd-sceneForm__cond">
                  <ConditionIcon id="invisible" />
                  {conditionLabel('invisible')}
                </span>
              </CheckBox>
            </div>
            <p className="rd-sceneForm__hint">{t('scene.token.conditionsHint')}</p>
          </fieldset>

          <fieldset className="rd-sceneForm__group" disabled={!isGm}>
            <legend>{t('scene.token.vision')}</legend>
            <CheckBox checked={hasSight} onChange={setHasSight}>
              {t('scene.token.hasSight')}
            </CheckBox>
            <div className="rd-sceneForm__grid">
              <label className="rd-sceneForm__field">
                <span>{t('scene.token.sightBright', { unit })}</span>
                <TextEdit look="filled" value={bright} onChange={setBright} inputMode="decimal" maxLength={8} />
              </label>
              <label className="rd-sceneForm__field">
                <span>{t('scene.token.sightDim', { unit })}</span>
                <TextEdit look="filled" value={dim} onChange={setDim} inputMode="decimal" maxLength={8} />
              </label>
              <label className="rd-sceneForm__field">
                <span>{t('scene.token.sightDark', { unit })}</span>
                <TextEdit look="filled" value={dark} onChange={setDark} inputMode="decimal" maxLength={8} />
              </label>
            </div>
            <div className="rd-sceneForm__grid">
              <label className="rd-sceneForm__field">
                <span>{t('scene.token.lightBright', { unit })}</span>
                <TextEdit look="filled" value={lightBright} onChange={setLightBright} inputMode="decimal" maxLength={8} />
              </label>
              <label className="rd-sceneForm__field">
                <span>{t('scene.token.lightDim', { unit })}</span>
                <TextEdit look="filled" value={lightDim} onChange={setLightDim} inputMode="decimal" maxLength={8} />
              </label>
            </div>
            <div className="rd-sceneForm__grid">
              <label className="rd-sceneForm__field">
                <span>{t('scene.token.angle')}</span>
                <TextEdit look="filled" value={angle} onChange={setAngle} inputMode="decimal" maxLength={5} />
              </label>
              <label className="rd-sceneForm__field">
                <span>{t('scene.token.lightAngle')}</span>
                <TextEdit look="filled" value={lightAngle} onChange={setLightAngle} inputMode="decimal" maxLength={5} />
              </label>
              <label className="rd-sceneForm__field">
                <span>{t('scene.token.facingMode')}</span>
                <ComboBox<FacingMode>
                  value={facingMode}
                  onChange={setFacingMode}
                  options={[
                    { value: 'rotate', label: t('scene.token.facingMode.rotate') },
                    { value: 'arrow', label: t('scene.token.facingMode.arrow') },
                  ]}
                />
              </label>
            </div>
            <CheckBox checked={lightOnly} onChange={setLightOnly}>
              {t('scene.token.lightOnly')}
            </CheckBox>
            <p className="rd-sceneForm__hint">{t('scene.token.rangesHint')}</p>
            {!isGm ? <p className="rd-sceneForm__hint">{t('scene.token.visionGmOnly')}</p> : null}
          </fieldset>

          <AuraFields draft={aura} onChange={setAura} unit={unit} onPickImage={() => setPickingAura(true)} />

          {isGm ? (
            <>
              <div className="rd-sceneForm__grid">
                <label className="rd-sceneForm__field">
                  <span>{t('scene.token.layer')}</span>
                  <ComboBox<TokenLayer>
                    value={layer}
                    onChange={setLayer}
                    options={[
                      { value: 'map', label: t('scene.layer.map') },
                      { value: 'tokens', label: t('scene.layer.tokens') },
                      { value: 'gm', label: t('scene.layer.gm') },
                    ]}
                  />
                </label>
              </div>
              <CheckBox checked={hidden} onChange={setHidden}>
                {t('scene.token.hidden')}
              </CheckBox>
              <div className="rd-sceneForm__field">
                <span>{t('scene.token.controllers')}</span>
                {people.length ? (
                  <div className="rd-sceneForm__people">
                    {people.map((p) => (
                      <CheckBox key={p.id} checked={controllers.includes(p.id)} onChange={(on) => setControllers((c) => (on ? [...c, p.id] : c.filter((x) => x !== p.id)))}>
                        {p.name}
                      </CheckBox>
                    ))}
                  </div>
                ) : (
                  <p className="rd-sceneForm__hint">{t('scene.token.nobody')}</p>
                )}
              </div>
            </>
          ) : null}
          <button type="submit" hidden />
        </form>
      </Dialog>
      {/* fora do formulário: um Enter no endereço da imagem não grava o token */}
      {pickingAura ? (
        <ImagePickerDialog
          title={t('scene.aura.image.pick')}
          initial={aura.url ?? undefined}
          onPick={(value) => {
            setPickingAura(false);
            const fileId = rpgDeskFileId(value);
            setAura((d) => (fileId ? { ...d, fileId, url: null } : { ...d, fileId: null, url: webImageUrl(value) }));
          }}
          onCancel={() => setPickingAura(false)}
        />
      ) : null}
    </>
  );
}

/**
 * O Invisível do token (BRAZ-24): liga ou desliga, e quem continua vendo, entre os participantes da
 * mesa. Os mestres e quem controla o token sempre o veem; quem não foi liberado nem recebe o token.
 * Quem já foi liberado e saiu da mesa continua na lista (a liberação é pela conta).
 */
export function InvisibleDialog({ token, onClose, onSave }: { token: SceneToken; onClose: () => void; onSave: (viewers: string[] | null) => void }) {
  const t = useT();
  const users = useApp((s) => s.users);
  const members = useApp((s) => s.members);
  const room = useApp((s) => s.room);
  const me = useApp((s) => s.user);
  // abrir o diálogo é para deixar invisível: já vem ligado (desmarcar tira)
  const [on, setOn] = useState<boolean>(true);
  const [viewers, setViewers] = useState<string[]>(token.invisible ?? []);
  const isGmUser = (id: string) => room?.ownerId === id || !!members[id]?.modes?.includes('gm');
  // os mestres, quem controla e eu mesmo sempre veem: não entram na escolha
  const people = [
    ...users.filter((u) => u.id !== me?.id && !isGmUser(u.id) && !token.controllers.includes(u.id)).map((u) => ({ id: u.id, name: members[u.id]?.nick ?? u.displayName })),
    ...viewers.filter((id) => !users.some((u) => u.id === id)).map((id) => ({ id, name: t('scene.invisible.away') })),
  ];
  return (
    <Dialog
      title={t('scene.invisible.title', { name: token.name || t('scene.token') })}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" onClick={() => onSave(on ? viewers : null)}>
            {t('scene.save')}
          </Button>
        </>
      }
    >
      <div className="rd-sceneForm">
        <CheckBox checked={on} onChange={setOn}>
          {t('scene.invisible.on')}
        </CheckBox>
        {on ? (
          <div className="rd-sceneForm__field">
            <span>{t('scene.invisible.viewers')}</span>
            {people.length ? (
              <div className="rd-sceneForm__people">
                {people.map((p) => (
                  <CheckBox key={p.id} checked={viewers.includes(p.id)} onChange={(yes) => setViewers((v) => (yes ? [...v, p.id] : v.filter((x) => x !== p.id)))}>
                    {p.name}
                  </CheckBox>
                ))}
              </div>
            ) : (
              <p className="rd-sceneForm__hint">{t('scene.invisible.nobody')}</p>
            )}
            <p className="rd-sceneForm__hint">{t('scene.invisible.always')}</p>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

export function SceneSettingsDialog({
  scene,
  onClose,
  write,
  onPickImage,
  onImportUvtt,
}: {
  scene: SceneModel;
  onClose: () => void;
  write: (ops: Op[]) => void;
  onPickImage: () => void;
  onImportUvtt: (file: File) => void;
}) {
  const t = useT();
  const [gridSize, setGridSize] = useState(String(scene.gridSize));
  const [cols, setCols] = useState(String(scene.cols));
  const [rows, setRows] = useState(String(scene.rows));
  const [gridVisible, setGridVisible] = useState(scene.gridVisible);
  const [gridColor, setGridColor] = useState(scene.gridColor);
  const [gridOpacity, setGridOpacity] = useState(scene.gridOpacity);
  const [cellDistance, setCellDistance] = useState(show(scene.cellDistance));
  const [cellUnit, setCellUnit] = useState(scene.cellUnit);
  const [fogEnabled, setFogEnabled] = useState(scene.fogEnabled);
  const [vision, setVision] = useState(scene.vision);
  const [fogSharing, setFogSharing] = useState(scene.fogSharing);
  const [ambientLight, setAmbientLight] = useState(scene.ambientLight);

  const int = (v: string, lo: number, hi: number, fallback: number) => {
    const n = Math.round(Number(v));
    return Number.isFinite(n) && n >= lo ? Math.min(hi, n) : fallback;
  };

  const save = () => {
    const distance = Number(cellDistance.replace(',', '.'));
    const attrs: Record<string, AttrValue | null> = {
      gridSize: int(gridSize, 16, 512, scene.gridSize),
      cols: int(cols, 1, 400, scene.cols),
      rows: int(rows, 1, 400, scene.rows),
      gridVisible,
      gridColor,
      gridOpacity,
      cellDistance: Number.isFinite(distance) && distance > 0 ? distance : SCENE_DEFAULTS.cellDistance,
      cellUnit: cellUnit.trim().slice(0, 8) || SCENE_DEFAULTS.cellUnit,
      fogEnabled,
      vision,
      fogSharing,
      ambientLight,
    };
    const current = scene as unknown as Record<string, AttrValue | null>;
    write(setOps('root', Object.fromEntries(Object.entries(attrs).filter(([k, v]) => v !== current[k]))));
    onClose();
  };

  return (
    <Dialog
      title={t('scene.settings')}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" onClick={save}>
            {t('scene.save')}
          </Button>
        </>
      }
    >
      <form
        className="rd-sceneForm"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div className="rd-sceneForm__field">
          <span>{t('scene.mapImage')}</span>
          <div className="rd-sceneForm__inline">
            <Button
              variant="secondary"
              onClick={() => {
                onClose();
                onPickImage();
              }}
            >
              {scene.bgFileId || scene.bgUrl ? t('scene.bg.replace') : t('scene.bg.choose')}
            </Button>
            {scene.bgFileId || scene.bgUrl ? (
              <Button
                variant="link"
                onClick={() => {
                  write(clearBackgroundOps());
                  onClose();
                }}
              >
                {t('scene.bg.remove')}
              </Button>
            ) : null}
            <label className="rd-sceneForm__file rd-btn rd-btn--secondary">
              {t('scene.uvtt.import')}
              <input
                type="file"
                accept=".uvtt,.dd2vtt,.df2vtt"
                hidden
                aria-label={t('scene.uvtt.import')}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  onClose();
                  onImportUvtt(file);
                }}
              />
            </label>
          </div>
        </div>
        <div className="rd-sceneForm__grid">
          <label className="rd-sceneForm__field">
            <span>{t('scene.cols')}</span>
            <TextEdit look="filled" value={cols} onChange={setCols} inputMode="numeric" maxLength={3} />
          </label>
          <label className="rd-sceneForm__field">
            <span>{t('scene.rows')}</span>
            <TextEdit look="filled" value={rows} onChange={setRows} inputMode="numeric" maxLength={3} />
          </label>
          <label className="rd-sceneForm__field">
            <span>{t('scene.gridSize')}</span>
            <TextEdit look="filled" value={gridSize} onChange={setGridSize} inputMode="numeric" maxLength={3} />
          </label>
        </div>
        <div className="rd-sceneForm__grid">
          <label className="rd-sceneForm__field">
            <span>{t('scene.cellDistance')}</span>
            <TextEdit look="filled" value={cellDistance} onChange={setCellDistance} inputMode="decimal" maxLength={6} />
          </label>
          <label className="rd-sceneForm__field">
            <span>{t('scene.cellUnit')}</span>
            <TextEdit look="filled" value={cellUnit} onChange={setCellUnit} maxLength={8} />
          </label>
          <label className="rd-sceneForm__field">
            <span>{t('scene.gridColor')}</span>
            <input className="rd-sceneForm__color" type="color" value={/^#[0-9a-f]{6}$/i.test(gridColor) ? gridColor : '#000000'} onChange={(e) => setGridColor(e.target.value)} />
          </label>
        </div>
        <label className="rd-sceneForm__field">
          <span>
            {t('scene.gridOpacity')} — {Math.round(gridOpacity * 100)}%
          </span>
          <input className="rd-range" type="range" min={0} max={1} step={0.05} value={gridOpacity} onChange={(e) => setGridOpacity(Number(e.target.value))} />
        </label>
        <CheckBox checked={gridVisible} onChange={setGridVisible}>
          {t('scene.gridVisible')}
        </CheckBox>
        <CheckBox checked={fogEnabled} onChange={setFogEnabled}>
          {t('scene.fog.enabled')}
        </CheckBox>
        <fieldset className="rd-sceneForm__group" disabled={!fogEnabled}>
          <legend>{t('scene.vision')}</legend>
          <CheckBox checked={vision} onChange={setVision}>
            {t('scene.vision.enabled')}
          </CheckBox>
          <div className="rd-sceneForm__grid">
            <label className="rd-sceneForm__field">
              <span>{t('scene.vision.ambient')}</span>
              <ComboBox<SceneModel['ambientLight']>
                value={ambientLight}
                onChange={setAmbientLight}
                options={(['intense', 'weak', 'none'] as const).map((v) => ({ value: v, label: t(`scene.vision.ambient.${v}`) }))}
              />
            </label>
            <label className="rd-sceneForm__field">
              <span>{t('scene.vision.sharing')}</span>
              <ComboBox<SceneModel['fogSharing']>
                value={fogSharing}
                onChange={setFogSharing}
                options={[
                  { value: 'individual', label: t('scene.vision.sharing.individual') },
                  { value: 'group', label: t('scene.vision.sharing.group') },
                ]}
              />
            </label>
          </div>
          {scene.vision ? (
            <div className="rd-sceneForm__inline">
              <Button
                variant="secondary"
                onClick={async () => {
                  const sure = await askConfirm({ title: t('scene.vision.resetMemory'), message: t('scene.vision.resetMemory.confirm'), action: t('scene.vision.resetMemory'), danger: true });
                  if (!sure) return;
                  write(resetMemoryOps(scene));
                  onClose();
                }}
              >
                {t('scene.vision.resetMemory')}
              </Button>
            </div>
          ) : null}
        </fieldset>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

type DoorState = 'closed' | 'open' | 'locked';

/**
 * Propriedades das paredes selecionadas (`docs/spec/scene-walls.md`): o tipo preenche movimento,
 * visão, direção e porta; dá para combinar à mão. Vale para todas as selecionadas; só paredes de um
 * segmento viram porta.
 *
 * As dicas embaixo explicam o que foi escolhido. Na visão limitada (terreno), a regra: a visão passa
 * pela primeira parede limitada e para na segunda. Uma linha aberta sozinha não esconde nada, e o
 * diálogo avisa (BRAZ-17).
 */
export function WallDialog({ walls, onClose, write, newId }: { walls: Wall[]; onClose: () => void; write: (ops: Op[]) => void; newId: () => string }) {
  const t = useT();
  const first = walls[0]!;
  const [props, setProps] = useState<WallProps>({ sight: first.sight, move: first.move, dir: first.dir, door: first.door });
  const [state, setState] = useState<DoorState>(first.open ? 'open' : first.locked ? 'locked' : 'closed');
  const canDoor = walls.every((w) => w.points.length === 4);
  /** alguma é uma linha aberta (não fecha no primeiro ponto) */
  const openLine = walls.some((w) => !w.closed);
  const preset = presetOf(props);

  const save = () => {
    const ops: Op[] = [];
    for (const wall of walls) {
      const next = { ...props, door: canDoor ? props.door : 'none' };
      ops.push(...setWallPropsOps(wall, next, newId()));
      if (next.door !== 'none') ops.push(...doorStateOps(wall, { open: state === 'open', locked: state === 'locked' }));
    }
    write(ops);
    onClose();
  };

  return (
    <Dialog
      title={t('scene.walls.properties')}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" onClick={save}>
            {t('scene.save')}
          </Button>
        </>
      }
    >
      <form
        className="rd-sceneForm"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        {walls.length > 1 ? <p className="rd-sceneForm__hint">{t('scene.walls.prop.many', { n: walls.length })}</p> : null}
        <label className="rd-sceneForm__field">
          <span>{t('scene.walls.prop.type')}</span>
          <ComboBox<WallPreset | 'custom'>
            value={preset ?? 'custom'}
            onChange={(v) => v !== 'custom' && setProps(WALL_PRESETS[v])}
            options={[
              ...PRESET_ORDER.filter((p) => canDoor || WALL_PRESETS[p].door === 'none').map((p) => ({ value: p, label: t(`scene.walls.preset.${p}`) })),
              ...(preset ? [] : [{ value: 'custom' as const, label: t('scene.walls.prop.custom') }]),
            ]}
          />
        </label>
        <CheckBox checked={props.move} onChange={(move) => setProps((p) => ({ ...p, move }))}>
          {t('scene.walls.prop.move')}
        </CheckBox>
        <div className="rd-sceneForm__grid">
          <label className="rd-sceneForm__field">
            <span>{t('scene.walls.prop.sight')}</span>
            <ComboBox<WallSight>
              value={props.sight}
              onChange={(sight) => setProps((p) => ({ ...p, sight }))}
              options={(['normal', 'limited', 'none'] as const).map((v) => ({ value: v, label: t(`scene.walls.prop.sight.${v}`) }))}
            />
          </label>
          <label className="rd-sceneForm__field">
            <span>{t('scene.walls.prop.dir')}</span>
            <ComboBox<WallDir>
              value={props.dir}
              onChange={(dir) => setProps((p) => ({ ...p, dir }))}
              options={(['both', 'left', 'right'] as const).map((v) => ({ value: v, label: t(`scene.walls.prop.dir.${v}`) }))}
            />
          </label>
        </div>
        <div className="rd-sceneForm__grid">
          <label className="rd-sceneForm__field">
            <span>{t('scene.walls.prop.door')}</span>
            <ComboBox<DoorKind>
              value={canDoor ? props.door : 'none'}
              onChange={(door) => setProps((p) => ({ ...p, door }))}
              options={(['none', ...(canDoor ? (['door', 'secret'] as const) : [])] as DoorKind[]).map((v) => ({ value: v, label: t(`scene.walls.prop.door.${v}`) }))}
            />
          </label>
          {canDoor && props.door !== 'none' ? (
            <label className="rd-sceneForm__field">
              <span>{t('scene.walls.prop.state')}</span>
              <ComboBox<DoorState> value={state} onChange={setState} options={(['closed', 'open', 'locked'] as const).map((v) => ({ value: v, label: t(`scene.walls.prop.state.${v}`) }))} />
            </label>
          ) : null}
        </div>
        {props.sight === 'limited' ? <p className="rd-sceneForm__hint">{t('scene.walls.prop.hint.terrain')}</p> : null}
        {props.sight === 'limited' && openLine ? (
          <p className="rd-sceneForm__hint is-warning" role="note">
            {t('scene.walls.prop.hint.terrainOpen')}
          </p>
        ) : null}
        {props.dir !== 'both' ? <p className="rd-sceneForm__hint">{t('scene.walls.prop.hint.dir')}</p> : null}
        {canDoor ? null : <p className="rd-sceneForm__hint">{t('scene.walls.prop.doorTwoPoints')}</p>}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
